import * as React from 'react';
import { Button, Tooltip } from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import type { Payload, SortOrder } from '../../../../shared/riskregister/types';
import type { GroupedRows } from '../hooks/useFilteredRows';
import { CategoryChip, DueChip, IdleChip, ScoreChip, SourceChip, TierChip } from '../common/Chips';

const COLUMNS: Array<{ label: string; sort: SortOrder }> = [
  { label: 'Ticket', sort: 'key' },
  { label: 'Priority', sort: 'worst' },
  { label: 'Summary', sort: 'worst' },
  { label: 'Risk category', sort: 'score' },
  { label: 'Score', sort: 'score' },
  { label: 'Due', sort: 'due' },
  { label: 'Due moves', sort: 'slips' },
  { label: 'Idle', sort: 'idle' },
  { label: 'Added', sort: 'entered' },
  { label: 'Status', sort: 'worst' },
  { label: 'Owner', sort: 'worst' },
  { label: 'Manager', sort: 'worst' },
];

function formattedDate(value: string | null): string {
  return value || '—';
}

export const ItemsTable: React.FunctionComponent<{
  payload: Payload;
  groups: GroupedRows[];
  sort: SortOrder;
  onSort: (sort: SortOrder) => void;
}> = ({ payload, groups, sort, onSort }) => {
  const count = groups.reduce((total, group) => total + group.rows.length, 0);

  if (!count) {
    return <p className="risk-register__empty">No register items match the current view.</p>;
  }

  return (
    <div className="risk-register__table-wrap">
      <p className="risk-register__table-summary">{count} item{count === 1 ? '' : 's'} in {groups.length} group{groups.length === 1 ? '' : 's'}.</p>
      <Table aria-label="Risk Register items" variant="compact">
        <Thead>
          <Tr>
            {COLUMNS.map((column) => (
              <Th key={column.label} scope="col">
                <Button
                  variant="plain"
                  className="risk-register__sort-button"
                  isInline
                  onClick={() => onSort(column.sort)}
                  aria-label={`Sort all items by ${column.label}`}
                  aria-pressed={sort === column.sort}
                >
                  {column.label}
                </Button>
              </Th>
            ))}
          </Tr>
        </Thead>
        {groups.map((group) => (
          <Tbody key={group.key}>
            <Tr className="risk-register__group-row">
              <Td colSpan={COLUMNS.length}>
                <div className="risk-register__group-header">
                  <strong>{group.label}</strong>
                  <span>{group.aggregate.open} open</span>
                  <span>{group.aggregate.overdue} overdue</span>
                  <span>{group.aggregate.atrisk} at risk</span>
                  <span>{group.aggregate.stale} idle</span>
                  <span>{group.aggregate.weighted} weighted risk</span>
                  {group.confidence && <SourceChip>Area confidence: {group.confidence}</SourceChip>}
                </div>
              </Td>
            </Tr>
            {group.rows.map((row) => (
              <Tr key={row.key}>
                <Td dataLabel="Ticket">
                  <a href={`${payload.jira}${row.key}`} target="_blank" rel="noreferrer">{row.key}</a>
                  {row.was && <span className="risk-register__was">Was {row.was}</span>}
                </Td>
                <Td dataLabel="Priority">{row.pri}</Td>
                <Td dataLabel="Summary">
                  <span className="risk-register__summary">{row.summary}</span>
                  <TierChip row={row} payload={payload} />
                </Td>
                <Td dataLabel="Risk category"><CategoryChip row={row} payload={payload} /></Td>
                <Td dataLabel="Score"><ScoreChip row={row} /></Td>
                <Td dataLabel="Due">
                  <DueChip row={row} />
                  <span className="risk-register__date">{formattedDate(row.due)}</span>
                </Td>
                <Td dataLabel="Due moves">
                  <Tooltip
                    content={
                      <div>
                        {row.due_slips} slipped, {row.due_pulled} pulled in, {row.due_set} set, {row.due_cleared} cleared.
                      </div>
                    }
                  >
                    <span>{row.due_slips} slip{row.due_slips === 1 ? '' : 's'} ({row.due_moves} move{row.due_moves === 1 ? '' : 's'})</span>
                  </Tooltip>
                </Td>
                <Td dataLabel="Idle"><IdleChip row={row} staleDays={payload.stale_days} stalledDays={payload.stalled_days} /></Td>
                <Td dataLabel="Added">{formattedDate(row.entered)}{row.entered_est && '*'}</Td>
                <Td dataLabel="Status">{row.status}</Td>
                <Td dataLabel="Owner">{row.assignee || <SourceChip>Unassigned</SourceChip>}</Td>
                <Td dataLabel="Manager">{row.mgr || <SourceChip>{payload.no_manager_label}</SourceChip>}</Td>
              </Tr>
            ))}
          </Tbody>
        ))}
      </Table>
    </div>
  );
};
